import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface ActiveSession {
  session_id: string;
  ip_address: string;
  city: string | null;
  state: string | null;
  device_type: string;
  platform: string;
  user_type: string;
  auth_id: number | null;
  first_seen: string;
  last_seen: string;
  // Novos campos de dispositivo detalhado
  device_brand?: string;
  device_model?: string;
  device_identifier?: string;
  system_version?: string;
  app_version?: string;
  app_build?: string;
  is_tablet?: boolean;
  device_display_name?: string;
}

interface ActiveSessionsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const API_BASE = '/analytics';

const ActiveSessionsModal: React.FC<ActiveSessionsModalProps> = ({ isOpen, onClose }) => {
  const [sessions, setSessions] = useState<ActiveSession[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      fetchSessions();
      // Atualiza a cada 10 segundos enquanto o modal estiver aberto
      const interval = setInterval(fetchSessions, 10000);
      return () => clearInterval(interval);
    }
  }, [isOpen]);

  const fetchSessions = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`${API_BASE}/active-sessions`);
      setSessions(response.data.data || []);
    } catch (error: any) {
      console.error('Erro ao buscar sessões ativas:', error);
      setError(error.message || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const getStatusLabel = (auth_id: number | null, user_type: string): string => {
    if (!auth_id) return 'Visitante';
    if (user_type === 'banda') return 'Banda Logada';
    if (user_type === 'empresa') return 'Empresa Logada';
    if (user_type === 'usuario') return 'Usuário Logado';
    return 'Logado';
  };

  const getStatusColor = (auth_id: number | null): string => {
    return auth_id ? '#4caf50' : '#ff9800';
  };

  const getDeviceLabel = (device_type: string): string => {
    const labels: { [key: string]: string } = {
      'desktop': '💻 Desktop',
      'mobile-unknown': '📱 Mobile',
      'ios': '📱 iPhone',
      'android': '📱 Android',
      'ios-tablet': '📲 iPad',
      'android-tablet': '📲 Android Tablet',
      'tablet': '📲 Tablet',
      'unknown': '❓ Desconhecido'
    };
    return labels[device_type] || device_type;
  };

  // Formata o dispositivo com detalhes se disponível
  const formatDeviceDisplay = (session: ActiveSession): React.ReactNode => {
    if (session.device_display_name) {
      return (
        <span title={session.device_display_name} style={styles.deviceDetailBadge}>
          {session.device_brand && session.device_model
            ? `${session.device_brand} ${session.device_model}`
            : session.device_display_name}
          {session.system_version && (
            <span style={styles.versionBadge}>v{session.system_version}</span>
          )}
        </span>
      );
    }
    return (
      <span style={styles.deviceBadge}>
        {getDeviceLabel(session.device_type)}
      </span>
    );
  };

  if (!isOpen) return null;

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.header}>
          <div>
            <h2 style={styles.title}>Sessões Ativas Agora</h2>
            <p style={styles.subtitle}>Atualizações nos últimos 5 minutos</p>
          </div>
          <button style={styles.closeButton} onClick={onClose}>✕</button>
        </div>

        {loading && sessions.length === 0 && (
          <div style={styles.loading}>Carregando...</div>
        )}

        {error && (
          <div style={styles.error}>
            <p>{error}</p>
            <button onClick={fetchSessions} style={styles.retryButton}>Tentar Novamente</button>
          </div>
        )}

        {!loading && !error && sessions.length === 0 && (
          <div style={styles.empty}>
            <p>Nenhuma sessão ativa nos últimos 5 minutos</p>
          </div>
        )}

        {sessions.length > 0 && (
          <div style={styles.content}>
            <div style={styles.summary}>
              <span style={styles.summaryText}>
                <strong>{sessions.length}</strong> {sessions.length === 1 ? 'sessão ativa' : 'sessões ativas'}
              </span>
              {loading && <span style={styles.updating}>Atualizando...</span>}
            </div>

            <div style={styles.tableWrapper}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>IP</th>
                    <th style={styles.th}>Cidade</th>
                    <th style={styles.th}>Estado</th>
                    <th style={styles.th}>Dispositivo</th>
                    <th style={styles.th}>Status</th>
                    <th style={styles.th}>Tipo</th>
                    <th style={styles.th}>Última Atividade</th>
                  </tr>
                </thead>
                <tbody>
                  {sessions.map((session, index) => (
                    <tr key={session.session_id} style={index % 2 === 0 ? styles.trEven : styles.trOdd}>
                      <td style={styles.td}>
                        <code style={styles.ipCode}>{session.ip_address}</code>
                      </td>
                      <td style={styles.td}>{session.city || 'N/A'}</td>
                      <td style={styles.td}>{session.state || 'N/A'}</td>
                      <td style={styles.td}>
                        {formatDeviceDisplay(session)}
                      </td>
                      <td style={styles.td}>
                        <span style={{
                          ...styles.statusBadge,
                          backgroundColor: getStatusColor(session.auth_id)
                        }}>
                          {getStatusLabel(session.auth_id, session.user_type)}
                        </span>
                      </td>
                      <td style={styles.td}>
                        {session.auth_id ? (
                          <span style={styles.userTypeBadge}>
                            {session.user_type === 'banda' ? '🎸 Banda' :
                             session.user_type === 'empresa' ? '🏢 Empresa' :
                             session.user_type === 'usuario' ? '👤 Usuário' :
                             session.user_type || 'N/A'}
                          </span>
                        ) : (
                          <span style={{...styles.userTypeBadge, color: '#999'}}>-</span>
                        )}
                      </td>
                      <td style={styles.td}>
                        {format(new Date(session.last_seen), 'HH:mm:ss', { locale: ptBR })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 9999,
    padding: '20px',
  } as React.CSSProperties,
  modal: {
    backgroundColor: 'white',
    borderRadius: '12px',
    width: '95%',
    maxWidth: '1200px',
    maxHeight: '85vh',
    overflow: 'auto',
    boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
  } as React.CSSProperties,
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: '20px 30px',
    borderBottom: '2px solid #eee',
    backgroundColor: 'white',
    position: 'sticky',
    top: 0,
    zIndex: 10,
  } as React.CSSProperties,
  title: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#333',
    margin: 0,
    marginBottom: '5px',
  } as React.CSSProperties,
  subtitle: {
    fontSize: '14px',
    color: '#666',
    margin: 0,
  } as React.CSSProperties,
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '28px',
    cursor: 'pointer',
    color: '#666',
    padding: '0',
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    transition: 'background 0.2s',
  } as React.CSSProperties,
  content: {
    padding: '0',
  } as React.CSSProperties,
  summary: {
    padding: '15px 30px',
    backgroundColor: '#f8f9fa',
    borderBottom: '1px solid #eee',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  } as React.CSSProperties,
  summaryText: {
    fontSize: '15px',
    color: '#333',
  } as React.CSSProperties,
  updating: {
    fontSize: '13px',
    color: '#1976d2',
    fontStyle: 'italic',
  } as React.CSSProperties,
  tableWrapper: {
    overflowX: 'auto',
    padding: '20px 30px',
  } as React.CSSProperties,
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '14px',
  } as React.CSSProperties,
  th: {
    textAlign: 'left',
    padding: '12px',
    borderBottom: '2px solid #ddd',
    backgroundColor: '#f9f9f9',
    fontWeight: 'bold',
    color: '#333',
    whiteSpace: 'nowrap',
  } as React.CSSProperties,
  td: {
    padding: '12px',
    borderBottom: '1px solid #eee',
  } as React.CSSProperties,
  trEven: {
    backgroundColor: '#fafafa',
  } as React.CSSProperties,
  trOdd: {
    backgroundColor: 'white',
  } as React.CSSProperties,
  ipCode: {
    fontFamily: 'monospace',
    fontSize: '13px',
    backgroundColor: '#f0f0f0',
    padding: '4px 8px',
    borderRadius: '4px',
    color: '#333',
  } as React.CSSProperties,
  deviceBadge: {
    fontSize: '13px',
    color: '#666',
  } as React.CSSProperties,
  deviceDetailBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '13px',
    color: '#333',
    fontWeight: '500',
  } as React.CSSProperties,
  versionBadge: {
    fontSize: '11px',
    color: '#888',
    backgroundColor: '#f0f0f0',
    padding: '2px 6px',
    borderRadius: '4px',
    marginLeft: '4px',
  } as React.CSSProperties,
  statusBadge: {
    display: 'inline-block',
    padding: '4px 12px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 'bold',
    color: 'white',
  } as React.CSSProperties,
  userTypeBadge: {
    fontSize: '13px',
    color: '#333',
    fontWeight: '500',
  } as React.CSSProperties,
  loading: {
    padding: '60px',
    textAlign: 'center',
    fontSize: '16px',
    color: '#666',
  } as React.CSSProperties,
  error: {
    padding: '60px',
    textAlign: 'center',
    color: '#d32f2f',
  } as React.CSSProperties,
  empty: {
    padding: '60px',
    textAlign: 'center',
    color: '#666',
  } as React.CSSProperties,
  retryButton: {
    marginTop: '15px',
    padding: '10px 20px',
    backgroundColor: '#1976d2',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    fontSize: '14px',
  } as React.CSSProperties,
};

export default ActiveSessionsModal;
